import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getThemes,
  createTheme,
  updateTheme,
  deleteTheme,
} from "../../utilities/themes-service";
import {
  getColours,
  createColour,
  updateColour,
  deleteColour,
} from "../../utilities/colours-service";

export default function AdminPage({ user }) {
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [colours, setColours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // New tag form state
  const [newTheme, setNewTheme] = useState("");
  const [newColour, setNewColour] = useState("");

  // Edit state
  const [editingThemeId, setEditingThemeId] = useState(null);
  const [editingThemeName, setEditingThemeName] = useState("");
  const [editingColourId, setEditingColourId] = useState(null);
  const [editingColourName, setEditingColourName] = useState("");

  // Delete confirmation state
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmDeleteType, setConfirmDeleteType] = useState(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  async function fetchData() {
    setLoading(true);
    setError("");
    try {
      const [themesData, coloursData] = await Promise.all([
        getThemes(),
        getColours(),
      ]);
      setThemes(Array.isArray(themesData) ? themesData : []);
      setColours(Array.isArray(coloursData) ? coloursData : []);
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  // Theme handlers
  async function handleAddTheme(e) {
    e.preventDefault();
    if (!newTheme.trim()) return;
    setError("");
    try {
      const created = await createTheme({ themeName: newTheme.trim() });
      if (created?._id) {
        setThemes([...themes, created]);
        setNewTheme("");
      } else {
        setError("Failed to create theme");
      }
    } catch {
      setError("Failed to create theme");
    }
  }

  async function handleUpdateTheme(id) {
    if (!editingThemeName.trim()) return;
    setError("");
    try {
      const updated = await updateTheme(id, {
        themeName: editingThemeName.trim(),
      });
      if (updated?._id) {
        setThemes(themes.map((t) => (t._id === id ? updated : t)));
        setEditingThemeId(null);
      } else {
        setError("Failed to update theme");
      }
    } catch {
      setError("Failed to update theme");
    }
  }

  async function handleDeleteTheme(id) {
    setError("");
    try {
      await deleteTheme(id);
      setThemes(themes.filter((t) => t._id !== id));
      setConfirmDeleteId(null);
      setConfirmDeleteType(null);
    } catch {
      setError("Failed to delete theme");
    }
  }

  // Colour handlers
  async function handleAddColour(e) {
    e.preventDefault();
    if (!newColour.trim()) return;
    setError("");
    try {
      const created = await createColour({ colourName: newColour.trim() });
      if (created?._id) {
        setColours([...colours, created]);
        setNewColour("");
      } else {
        setError("Failed to create colour");
      }
    } catch {
      setError("Failed to create colour");
    }
  }

  async function handleUpdateColour(id) {
    if (!editingColourName.trim()) return;
    setError("");
    try {
      const updated = await updateColour(id, {
        colourName: editingColourName.trim(),
      });
      if (updated?._id) {
        setColours(colours.map((c) => (c._id === id ? updated : c)));
        setEditingColourId(null);
      } else {
        setError("Failed to update colour");
      }
    } catch {
      setError("Failed to update colour");
    }
  }

  async function handleDeleteColour(id) {
    setError("");
    try {
      await deleteColour(id);
      setColours(colours.filter((c) => c._id !== id));
      setConfirmDeleteId(null);
      setConfirmDeleteType(null);
    } catch {
      setError("Failed to delete colour");
    }
  }

  function startDeleteConfirm(id, type) {
    setConfirmDeleteId(id);
    setConfirmDeleteType(type);
  }

  function cancelDelete() {
    setConfirmDeleteId(null);
    setConfirmDeleteType(null);
  }

  if (!user?.isAdmin) return null;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-bold mb-8">Tag management</h1>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Themes Section */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Themes</h2>

          <form onSubmit={handleAddTheme} className="flex gap-2 mb-4">
            <input
              type="text"
              value={newTheme}
              onChange={(e) => setNewTheme(e.target.value)}
              placeholder="New theme name"
              className="border rounded px-3 py-2 flex-1"
            />
            <button
              type="submit"
              className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
            >
              Add
            </button>
          </form>

          <ul className="space-y-2">
            {themes.map((theme) => (
              <li
                key={theme._id}
                className="flex items-center gap-2 border rounded px-3 py-2"
              >
                {editingThemeId === theme._id ? (
                  <>
                    <input
                      type="text"
                      value={editingThemeName}
                      onChange={(e) => setEditingThemeName(e.target.value)}
                      className="border rounded px-2 py-1 flex-1"
                      autoFocus
                    />
                    <button
                      onClick={() => handleUpdateTheme(theme._id)}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingThemeId(null)}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Cancel
                    </button>
                  </>
                ) : confirmDeleteId === theme._id &&
                  confirmDeleteType === "theme" ? (
                  <>
                    <span className="flex-1 text-red-600">
                      Delete "{theme.themeName}"?
                    </span>
                    <button
                      onClick={() => handleDeleteTheme(theme._id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={cancelDelete}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1">{theme.themeName}</span>
                    <button
                      onClick={() => {
                        setEditingThemeId(theme._id);
                        setEditingThemeName(theme.themeName);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => startDeleteConfirm(theme._id, "theme")}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </>
                )}
              </li>
            ))}
            {themes.length === 0 && (
              <li className="text-gray-400 italic">No themes yet</li>
            )}
          </ul>
        </section>

        {/* Colours Section */}
        <section>
          <h2 className="text-xl font-semibold mb-4">Colours</h2>

          <form onSubmit={handleAddColour} className="flex gap-2 mb-4">
            <input
              type="text"
              value={newColour}
              onChange={(e) => setNewColour(e.target.value)}
              placeholder="New colour name"
              className="border rounded px-3 py-2 flex-1"
            />
            <button
              type="submit"
              className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800"
            >
              Add
            </button>
          </form>

          <ul className="space-y-2">
            {colours.map((colour) => (
              <li
                key={colour._id}
                className="flex items-center gap-2 border rounded px-3 py-2"
              >
                {editingColourId === colour._id ? (
                  <>
                    <input
                      type="text"
                      value={editingColourName}
                      onChange={(e) => setEditingColourName(e.target.value)}
                      className="border rounded px-2 py-1 flex-1"
                      autoFocus
                    />
                    <button
                      onClick={() => handleUpdateColour(colour._id)}
                      className="text-green-600 hover:text-green-800 text-sm font-medium"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingColourId(null)}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Cancel
                    </button>
                  </>
                ) : confirmDeleteId === colour._id &&
                  confirmDeleteType === "colour" ? (
                  <>
                    <span className="flex-1 text-red-600">
                      Delete "{colour.colourName}"?
                    </span>
                    <button
                      onClick={() => handleDeleteColour(colour._id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={cancelDelete}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1">{colour.colourName}</span>
                    <button
                      onClick={() => {
                        setEditingColourId(colour._id);
                        setEditingColourName(colour.colourName);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => startDeleteConfirm(colour._id, "colour")}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Delete
                    </button>
                  </>
                )}
              </li>
            ))}
            {colours.length === 0 && (
              <li className="text-gray-400 italic">No colours yet</li>
            )}
          </ul>
        </section>
      </div>
    </div>
  );
}
